import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { facilityAPI } from '../services/api';
import { Building2, Plus, Edit2, Trash2, FileText, AlertCircle, Lock, X } from 'lucide-react';
import { toast } from 'react-toastify';

function Facilities() {
  const [facilities, setFacilities] = useState([]);
  const [usage, setUsage] = useState({ current: 0, limit: 1, atLimit: false });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [formData, setFormData] = useState({
    facilityName: '', facilityAddress: '', facilityType: '',
    state: '', epaId: '', naicsCode: '',
    contactName: '', contactEmail: '', contactPhone: '',
  });

  useEffect(() => { loadFacilities(); }, []);

  const loadFacilities = async () => {
    try {
      const response = await facilityAPI.getAll();
      setFacilities(response.data.facilities || []);
      if (response.data.usage) setUsage(response.data.usage);
    } catch (error) {
      toast.error('Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFacility) {
        await facilityAPI.update(editingFacility.id, formData);
        toast.success('Facility updated!');
      } else {
        await facilityAPI.create(formData);
        toast.success('Facility created!');
      }
      setShowAddModal(false);
      setEditingFacility(null);
      resetForm();
      loadFacilities();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to save facility';
      toast.error(msg);
    }
  };

  const handleEdit = (facility) => {
    setEditingFacility(facility);
    setFormData({
      facilityName: facility.name || facility.facility_name || '',
      facilityAddress: facility.address || '',
      facilityType: facility.type || '',
      state: facility.state || '',
      epaId: facility.epaId || '',
      naicsCode: facility.naicsCode || '',
      contactName: facility.contactName || '',
      contactEmail: facility.contactEmail || '',
      contactPhone: facility.contactPhone || '',
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this facility? This will also delete all associated documents and tasks.')) return;
    try {
      await facilityAPI.delete(id);
      toast.success('Facility deleted');
      loadFacilities();
    } catch (error) {
      toast.error('Failed to delete facility');
    }
  };

  const resetForm = () => setFormData({
    facilityName: '', facilityAddress: '', facilityType: '',
    state: '', epaId: '', naicsCode: '',
    contactName: '', contactEmail: '', contactPhone: '',
  });

  const handleAddClick = () => {
    if (usage.atLimit) return;
    setEditingFacility(null);
    resetForm();
    setShowAddModal(true);
  };

  const usStates = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#64748b' }}>Loading facilities...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="page-title">Facilities</div>
          <div className="page-subtitle">Manage your regulated facilities and permits</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button
            onClick={handleAddClick}
            disabled={usage.atLimit}
            className={`btn ${usage.atLimit ? 'btn-ghost' : 'btn-primary'}`} style={{ opacity: usage.atLimit ? 0.5 : 1, cursor: usage.atLimit ? 'not-allowed' : 'pointer' }}
          >
            {usage.atLimit ? <Lock size={16} /> : <Plus size={18} />}
            Add Facility
          </button>
          {/* Usage indicator */}
          {usage.limit < 999 && (
            <div style={{ fontSize: 14, color: usage.atLimit ? '#EF4444' : '#64748b' }}>
              {usage.current} of {usage.limit} {usage.limit === 1 ? 'facility' : 'facilities'} used
              {!usage.atLimit && (
                <div style={{ marginTop: 3, height: 3, background: '#e2e8f0', borderRadius: 2, width: 120 }}>
                  <div style={{ height: '100%', borderRadius: 2, background: usage.current / usage.limit > 0.8 ? '#F59E0B' : '#0066FF', width: `${Math.min((usage.current / usage.limit) * 100, 100)}%` }} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Limit reached banner */}
      {usage.atLimit && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#92400E' }}>🔒 Facility limit reached</div>
            <div style={{ fontSize: 14, color: '#92400E', marginTop: 3 }}>Your current plan includes {usage.limit} {usage.limit === 1 ? 'facility' : 'facilities'}. Contact us to upgrade and add more.</div>
          </div>
          <a href="mailto:sales@aircomply.com" style={{ padding: '8px 16px', background: '#0066FF', color: 'white', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Contact Us to Upgrade
          </a>
        </div>
      )}

      {/* Empty state */}
      {facilities.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <Building2 size={56} color="#cbd5e1" style={{ margin: '0 auto 16px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 8 }}>No facilities yet</div>
          <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>Add your first facility to start managing air permit compliance</p>
          <button onClick={handleAddClick} style={{ padding: '10px 24px', background: '#0066FF', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>
            Add Your First Facility
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
          {facilities.map((facility) => (
            <div key={facility.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 36, height: 36, background: '#EFF6FF', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Building2 size={18} color="#0066FF" />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', margin: '0 0 2px', fontFamily: 'var(--font-body)' }}>{facility.name || facility.facility_name}</div>
                    {facility.state && <div style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-body)' }}>{facility.state}{facility.type ? ` · ${facility.type}` : ''}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => handleEdit(facility)} className='icon-btn'><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(facility.id)} className='icon-btn' style={{ color: '#DC2626', borderColor: '#FECACA' }}><Trash2 size={14} /></button>
                </div>
              </div>

              {facility.address && <p style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>📍 {facility.address}</p>}
              {facility.epaId && <p style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>🏛️ EPA ID: {facility.epaId}</p>}

              {/* Overdue alert */}
              {facility.overdueCount > 0 && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '6px 10px', marginBottom: 10, fontSize: 14, color: '#991B1B', fontWeight: 600 }}>
                  🚨 {facility.overdueCount} overdue {facility.overdueCount === 1 ? 'task' : 'tasks'}
                </div>
              )}

              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#64748b', marginBottom: 12 }}>
                <span><FileText size={12} style={{ display: 'inline', marginRight: 4 }} />{facility.documentCount || 0} documents</span>
                <span><AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} />{facility.taskCount || 0} tasks</span>
              </div>

              <Link to={`/documents/upload?facilityId=${facility.id}`} style={{ display: 'block', textAlign: 'center', padding: '8px', background: '#EFF6FF', color: '#0066FF', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                Upload Permit
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: 'var(--color-text-primary)' }}>{editingFacility ? 'Edit Facility' : 'Add New Facility'}</div>
              <button onClick={() => { setShowAddModal(false); setEditingFacility(null); }} className='icon-btn'><X size={16} /></button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '14px 18px' }}>
              <div style={{ marginBottom: 14 }}>
                <label className='form-label'>Facility Name *</label>
                <input type="text" required value={formData.facilityName} onChange={e => setFormData({...formData, facilityName: e.target.value})} placeholder="Main Manufacturing Plant" style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--color-card-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className='form-label'>Address</label>
                <input type="text" value={formData.facilityAddress} onChange={e => setFormData({...formData, facilityAddress: e.target.value})} placeholder="123 Industrial Blvd, City, State 12345" style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--color-card-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className='form-label'>State</label>
                  <select value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}>
                    <option value="">Select State</option>
                    {usStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className='form-label'>Facility Type</label>
                  <input type="text" value={formData.facilityType} onChange={e => setFormData({...formData, facilityType: e.target.value})} placeholder="Manufacturing, Power Plant..." style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--color-card-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className='form-label'>EPA ID</label>
                  <input type="text" value={formData.epaId} onChange={e => setFormData({...formData, epaId: e.target.value})} placeholder="EPA123456789" style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--color-card-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
                </div>
                <div>
                  <label className='form-label'>NAICS Code</label>
                  <input type="text" value={formData.naicsCode} onChange={e => setFormData({...formData, naicsCode: e.target.value})} placeholder="325211" style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--color-card-border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'var(--font-body)' }} />
                </div>
              </div>

              {/* Contact info */}
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '11px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Contact Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Contact Name</label>
                    <input type="text" value={formData.contactName} onChange={e => setFormData({...formData, contactName: e.target.value})} placeholder="Jane Smith" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Contact Email</label>
                    <input type="email" value={formData.contactEmail} onChange={e => setFormData({...formData, contactEmail: e.target.value})} placeholder="jane@company.com" style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => { setShowAddModal(false); setEditingFacility(null); }} className='btn btn-ghost'>Cancel</button>
                <button type="submit" className='btn btn-primary'>{editingFacility ? 'Update' : 'Create'} Facility</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Facilities;
